import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const STATUS_META = {
  open:      { label: 'Open',      dot: '#F5A800' },
  finalized: { label: 'Finalized', dot: '#15803D' },
};

const QuoteSearch = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ quote_number: '', date_from: '', date_to: '' });

  useEffect(() => {
    const stored = localStorage.getItem('selected_customer');
    if (!stored) { navigate('/select-customer'); return; }
    const c = JSON.parse(stored);
    setCustomer(c);
    fetchQuotes(c.customer_id, {});
  }, [navigate]);

  const fetchQuotes = async (customerId, f) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ customer_id: customerId });
      if (f.quote_number) params.append('quote_number', f.quote_number);
      if (f.date_from) params.append('date_from', f.date_from);
      if (f.date_to) params.append('date_to', f.date_to);
      const res = await API.get(`/quotes?${params}`);
      setQuotes(res.data);
    } catch {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { if (customer) fetchQuotes(customer.customer_id, filters); };
  const handleClear = () => {
    setFilters({ quote_number: '', date_from: '', date_to: '' });
    if (customer) fetchQuotes(customer.customer_id, {});
  };

  const inputStyle = {
    padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: '6px',
    fontSize: '14px', outline: 'none'
  };

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600' }}>Quotes</h1>
          {customer && <p style={{ color: '#5B6B7F', fontSize: '14px' }}>Customer: <strong>{customer.name}</strong></p>}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/dashboard')} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
            ← Dashboard
          </button>
          <button onClick={() => navigate('/quotes/new')} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '8px 16px' }}>
            + New Quote
          </button>
        </div>
      </div>

      {/* Search filters */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '16px 20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Quote #</label>
            <input
              style={{ ...inputStyle, width: '160px' }}
              placeholder="QUO-00001"
              value={filters.quote_number}
              onChange={e => setFilters(prev => ({ ...prev, quote_number: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>From Date</label>
            <input type="date" style={{ ...inputStyle, width: '160px' }} value={filters.date_from} onChange={e => setFilters(prev => ({ ...prev, date_from: e.target.value }))} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>To Date</label>
            <input type="date" style={{ ...inputStyle, width: '160px' }} value={filters.date_to} onChange={e => setFilters(prev => ({ ...prev, date_to: e.target.value }))} />
          </div>
          <button onClick={handleSearch} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '8px 20px' }}>
            Search
          </button>
          <button onClick={handleClear} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
            Clear
          </button>
        </div>
      </div>

      {/* Results table */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0' }}>
          <p style={{ fontSize: '14px', color: '#5B6B7F' }}>
            {loading ? 'Loading...' : `${quotes.length} quote${quotes.length !== 1 ? 's' : ''} found`}
          </p>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr>
              {['Status', 'Quote #', 'Date', 'Valid Until', 'Bill To', 'Est. Total', 'Actions'].map(h => (
                <th key={h} style={{
                  padding: '10px 12px', background: '#1A2A3A', color: 'white',
                  fontSize: '13px', fontWeight: '500', textAlign: h === 'Status' ? 'center' : 'left',
                  borderRight: '1px solid #F5A800', width: h === 'Status' ? '70px' : undefined
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {quotes.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#5B6B7F' }}>
                  No quotes found
                </td>
              </tr>
            ) : (
              quotes.map(q => {
                const meta = STATUS_META[q.status] || STATUS_META.open;
                return (
                  <tr key={q.quote_id} className="fcc-row-hover" style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/quotes/${q.quote_id}`)}
                  >
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <span title={meta.label} style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: meta.dot }} />
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0', fontWeight: '600', color: '#F5A800' }}>
                      {q.quote_number}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0' }}>
                      {new Date(q.quote_date).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0', color: '#5B6B7F' }}>
                      {q.valid_until ? new Date(q.valid_until).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0' }}>{q.bill_to}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0', fontWeight: '600', color: '#15803D' }}>
                      ${parseFloat(q.amount).toFixed(2)}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0' }}>
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/quotes/${q.quote_id}`); }}
                        className="fcc-press"
                        style={{ background: '#F5A800', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '6px' }}
                      >
                        View
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/quotes/${q.quote_id}/edit`); }}
                        className="fcc-press"
                        style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuoteSearch;
